import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent, EVENT_TYPE_PAGE, trackGA4Event } from '../utils/trackEvent.jsx';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CREDIBILITY_CONTENT } from '../config/credibility.config.jsx';
import {
  TrendingUp, 
  Shield, 
  ArrowRight,
  Target,
  Zap,
  Globe,
  CheckCircle2,
  Clock,
  DollarSign,
  X,
  AlertCircle
} from 'lucide-react';

// Helper component to render text with styled HTML tags
const StyledText = ({ text, className = "" }) => {
  // Parse <b> and <strong> tags and replace with styled spans
  // Use a regex that matches both <b>...</b> and <strong>...</strong>
  const parts = text.split(/(<(?:b|strong)>.*?<\/(?:b|strong)>)/gi);
  
  return (
    <span className={className}>
      {parts.map((part, index) => {
        // Check if part is a bold tag (case-insensitive)
        const boldMatch = part.match(/<(?:b|strong)>(.*?)<\/(?:b|strong)>/i);
        if (boldMatch) {
          const boldText = boldMatch[1];
          return (
            <strong key={index} className="text-[#1a4a1a] font-bold">
              {boldText}
            </strong>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </span>
  );
};

const PartnerWithUs = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
    
    // SEO Meta Tags
    document.title = 'Launch Islamic Financing - HalalCred | CDFI & Capital Provider Enablement';
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Launch Islamic financing through your CDFI or capital program. HalalCred helps institutions bring riba-free financing products to market with enablement, training, and a clear onboarding path to launch and scale impact.');
    
    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'CDFI Islamic financing, Islamic financing products, riba-free financing, halal financing, CDFI partners, capital providers, financial inclusion, underserved Muslim communities, Islamic financing enablement');
    
    // Open Graph tags for social sharing
    const ogTags = [
      { property: 'og:title', content: 'Launch Islamic Financing - HalalCred | CDFI & Capital Provider Enablement' },
      { property: 'og:description', content: 'HalalCred helps CDFIs and capital providers launch Islamic financing products and expand access for underserved Muslim communities.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.href }
    ];
    
    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', tag.content);
    });
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.href);
  }, []);

  // Calendly link - same env var + URL parsing behavior as SetupMeetingModal
  const CALENDLY_LINK_RAW = process.env.REACT_APP_CALENDLY_LINK || 'rumi-sabinayasmin/30min';

  const getCalendlyPath = (link) => {
    if (!link) return '';
    if (link.startsWith('https://calendly.com/')) {
      return link.replace('https://calendly.com/', '');
    }
    if (link.startsWith('http://calendly.com/')) {
      return link.replace('http://calendly.com/', '');
    }
    if (link.startsWith('calendly.com/')) {
      return link.replace('calendly.com/', '');
    }
    return link;
  };

  const handleBookDiscoveryCall = () => {
    trackGA4Event('partner_landing_cta_clicked', {
      button_location: 'hero_section',
      button_text: 'Book a Discovery Call'
    });

    const calendlyPath = getCalendlyPath(CALENDLY_LINK_RAW);
    if (!calendlyPath || calendlyPath.trim() === '') {
      alert('Calendly link is not configured. Please set REACT_APP_CALENDLY_LINK.');
      return;
    }

    const calendlyUrl = `https://calendly.com/${calendlyPath}`;
    window.open(calendlyUrl, '_blank', 'noopener,noreferrer');
  };

  const handleStartPartnerApplication = () => {
    trackGA4Event('partner_landing_cta_clicked', {
      button_location: 'hero_section',
      button_text: 'Launch Islamic Financing'
    });
    navigate('/partner-signup');
  };

  const handleLearnMore = () => {
    trackGA4Event('partner_landing_cta_clicked', {
      button_location: 'hero_section',
      button_text: 'Learn More'
    });
    // Scroll to benefits section
    document.getElementById('benefits')?.scrollIntoView({ behavior: 'smooth' });
  };

  const benefits = [
    {
      icon: Target,
      title: 'Market-Proven',
      description: '$20M+ originated. 100% portfolio performance. Ready to deploy.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50'
    },
    {
      icon: Shield,
      title: 'Complete Product Frameworks',
      description: 'Templates, documentation, and Shariah certification—all included.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50'
    },
    {
      icon: Zap,
      title: 'Launch Faster',
      description: 'Skip years of development. Operationalize within your existing systems.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50'
    },
    {
      icon: TrendingUp,
      title: 'Reduce Complexity',
      description: 'Standardized processes eliminate legal and operational barriers.',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Schedule Meeting',
      description: 'Meet with our sales team to discuss partnership goals and implementation timeline.'
    },
    {
      step: '02',
      title: 'Sign Up & Partnership Agreement',
      description: 'Create your partner account and execute the Partnership Agreement to begin.'
    },
    {
      step: '03',
      title: 'Prepare Documentation',
      description: 'Use our standardized templates to complete required business documentation and product materials.'
    },
    {
      step: '04',
      title: 'Shariah Certification',
      description: 'Complete certification review with our Shariah advisory board to ensure compliance.'
    },
    {
      step: '05',
      title: 'Launch to Market',
      description: 'Begin serving your community with certified Islamic financing products and expand access to halal capital.'
    }
  ];

  const testimonials = [
    {
      quote: "Partnering with HalalCred has transformed our ability to serve the Muslim community with authentic Islamic financing solutions. The platform's commitment to following Islamic principles gives our customers confidence.",
      author: "Mufti Ibrahim Essa",
      title: "CEO @ Alhamd Sharia Advisory Services",
      rating: 5
    },
    {
      quote: "The training and resources provided by HalalCred have been invaluable. Our team now confidently explains Islamic financing principles to customers, leading to increased trust and business growth.",
      author: "Partner Testimonial",
      title: "Financial Institution Partner",
      rating: 5
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-screen flex items-center" aria-label="Hero section">
        {/* Background Image with Brightness Enhancement */}
        <div 
          className="absolute inset-0 z-0 brightness-110 contrast-105"
          style={{ 
            backgroundImage: `url("/uploads/halalcred-hero.png")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        {/* Professional Gradient Overlay - Trustworthy & Institution-Focused */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/45 to-black/55" />
        
        {/* Subtle Green Accent Overlay for Brand Consistency */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a4a1a]/10 via-transparent to-emerald-600/10" />
        
        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32 w-full">
          <div className="text-center animate-fade-in">
            
            {/* Main Brand - Heading block */}
            <div className="space-y-5 md:space-y-6 mb-6 md:mb-8">
              {/* Shariah-Compliant Badge */}
              <div className="flex items-center justify-center gap-2">
                <Shield className="w-4 h-4 md:w-5 md:h-5 text-emerald-300" />
                <span className="text-sm md:text-base text-emerald-300 font-medium">
                  Islamic Financing
                </span>
              </div>
              
              {/* Main Heading - Multi-line with Color Variations */}
              <div className="space-y-1 md:space-y-2">
                <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight">
                  <span className="text-white">The National Platform for</span>
                </h1>
                <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight">
                  <span className="text-emerald-400">Riba-Free, </span>
                  <span className="text-yellow-300">Mission-</span>
                </h1>
                <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] tracking-tight">
                  <span className="text-yellow-300">Driven </span>
                  <span className="text-white">Capital</span>
                </h1>
              </div>
            </div>
            
            {/* Description - Optimal line length for readability */}
            <div className="max-w-2xl mx-auto px-4 mb-10 md:mb-12">
              <p className="text-base md:text-lg lg:text-xl text-white/90 leading-relaxed">
                HalalCred connects Muslim and non-Muslim businesses, nonprofits, and institutions to Islamic financing delivered through trusted CDFIs — backed by <span className="font-semibold text-white">Craft3's proven track record</span> in Islamic financing and community lending.
              </p>
            </div>
            
            {/* Powerful Tagline - Distinct visual block */}
            <div className="mb-10 md:mb-12">
              <div className="inline-block px-6 py-4 md:px-10 md:py-5 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <p className="text-lg md:text-xl lg:text-2xl font-semibold text-white leading-snug">
                  <span className="text-emerald-300">No interest.</span> <span className="text-white">No compromise.</span> <span className="text-emerald-300">Real capital.</span> <span className="text-white">Real impact.</span>
                </p>
              </div>
            </div>
            
            {/* CTA Buttons - Clear separation for primary action */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={handleBookDiscoveryCall}
                size="lg"
                className="group bg-white text-[#1a4a1a] hover:bg-gray-50 px-8 md:px-10 py-5 md:py-6 text-base md:text-lg font-bold shadow-2xl hover:shadow-[0_8px_30px_rgba(255,255,255,0.4)] hover:scale-105 transition-all duration-300 rounded-xl min-w-[240px]"
              >
                Book a Call With Our Team
                <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-16 md:py-24 bg-white" aria-label="Value proposition">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a4a1a] mb-4">
              Tap Into Underserved Markets With Islamic Financing
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
              Everything you need to launch Islamic financing—proven product frameworks, documentation, and Shariah certification—without the complexity or years of development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border border-emerald-100 bg-white hover:border-emerald-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${benefit.bgColor} rounded-lg flex items-center justify-center mb-4`}>
                      <Icon className={`w-6 h-6 ${benefit.color}`} />
                    </div>
                    <h3 className="text-lg font-bold text-[#1a4a1a] mb-2">{benefit.title}</h3>
                    <p className="text-sm text-slate-600 leading-snug">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 md:py-28 bg-slate-50" aria-label="Build vs Partner comparison">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a4a1a] mb-4">
              Why Partner with HalalCred?
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
              A clear comparison of building in-house versus partnering with our platform
            </p>
          </div>

          {/* Professional Comparison Table */}
          <div className="bg-white rounded-xl shadow-lg border border-emerald-100/80 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-emerald-100">
                    <th className="px-2 py-2 md:px-4 lg:px-6 md:py-4 text-left text-xs md:text-sm font-semibold text-[#1a4a1a] uppercase tracking-wider">Factor</th>
                    <th className="px-2 py-2 md:px-4 lg:px-6 md:py-4 text-center text-xs md:text-sm font-semibold text-slate-700 uppercase tracking-wider">Build In-House</th>
                    <th className="px-2 py-2 md:px-4 lg:px-6 md:py-4 text-center text-xs md:text-sm font-semibold text-[#1a4a1a] uppercase tracking-wider bg-emerald-50">
                      Partner with HalalCred
                      <span className="block mt-0.5 text-xs font-normal text-emerald-700 normal-case">Recommended</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-50">
                  <tr className="hover:bg-emerald-50/30 transition-colors">
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm font-medium text-[#1a4a1a]">Upfront Development Cost</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center text-slate-600">$100K+</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center font-semibold text-emerald-700 bg-emerald-50/50">Significantly lower</td>
                  </tr>
                  <tr className="hover:bg-emerald-50/30 transition-colors">
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm font-medium text-[#1a4a1a]">Time to Market</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center text-slate-600">6-12 months</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center font-semibold text-emerald-700 bg-emerald-50/50">60-90 days</td>
                  </tr>
                  <tr className="hover:bg-emerald-50/30 transition-colors">
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm font-medium text-[#1a4a1a]">Ready-to-Deploy Products</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center text-slate-600">Build from scratch</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center font-semibold text-emerald-700 bg-emerald-50/50">Included</td>
                  </tr>
                  <tr className="hover:bg-emerald-50/30 transition-colors">
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm font-medium text-[#1a4a1a]">Compliance & Certification</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center text-slate-600">Hire Shariah advisor</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center font-semibold text-emerald-700 bg-emerald-50/50">Dedicated Shariah advisor</td>
                  </tr>
                  <tr className="hover:bg-emerald-50/30 transition-colors">
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm font-medium text-[#1a4a1a]">Marketing & Launch Support</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center text-slate-600">Internal resources only</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center font-semibold text-emerald-700 bg-emerald-50/50">Included</td>
                  </tr>
                  <tr className="hover:bg-emerald-50/30 transition-colors">
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm font-medium text-[#1a4a1a]">Risk Level</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center text-red-600 font-medium">High - unproven</td>
                    <td className="px-2 py-2 md:px-4 lg:px-6 md:py-3 text-xs md:text-sm text-center font-semibold text-emerald-700 bg-emerald-50/50">Low - Proven Institutional Experience</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility Band Section */}
      <section className="py-12 md:py-16 bg-white" aria-label="Credibility and trust indicators">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#1a4a1a] mb-5 leading-tight">
              {CREDIBILITY_CONTENT.headline}
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
              <StyledText text={CREDIBILITY_CONTENT.description} />
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-10 lg:p-12 border border-emerald-100 shadow-sm">
            <h3 className="text-xl md:text-2xl font-bold text-[#1a4a1a] mb-8 text-center">
              {CREDIBILITY_CONTENT.trackRecordTitle}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-10">
              {CREDIBILITY_CONTENT.metrics.map((metric, index) => (
                <div key={index} className="flex items-start gap-4 group">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-emerald-600 flex items-center justify-center group-hover:bg-emerald-700 transition-colors">
                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-base md:text-lg text-slate-700 leading-relaxed group-hover:text-slate-900 transition-colors">
                    {metric}
                  </p>
                </div>
              ))}
            </div>

            <div className="pt-8 border-t border-slate-200">
              <p className="text-lg md:text-xl font-semibold text-[#1a4a1a] text-center leading-relaxed">
                <StyledText text={CREDIBILITY_CONTENT.closingStatement} />
              </p>
            </div>
          </div>
        </div>
      </section>

      

      {/* How It Works Section */}
      <section id="benefits" className="py-16 md:py-24 bg-emerald-50/40" aria-label="How partnership works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a4a1a] mb-4">
              Your Path to Launching Islamic Financing
            </h2>
            <p className="text-xl text-slate-700 max-w-3xl mx-auto leading-relaxed">
              Get started in five straightforward steps—from signup to market launch
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all border border-emerald-100 hover:border-emerald-200">
                  <div className="text-4xl font-bold text-emerald-600 mb-4">{step.step}</div>
                  <h3 className="text-xl font-bold text-[#1a4a1a] mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-emerald-400" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-lg text-slate-700 mb-6">
              Ready to see the difference? Let's discuss how partnership can accelerate your Islamic financing launch.
            </p>
            <Button
              onClick={handleBookDiscoveryCall}
              size="lg"
              className="group bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              Schedule Partnership Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* Temporarily commented out
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a4a1a] mb-4">
              What Our Partners Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join a community of satisfied partners growing their business with HalalCred
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-2 border-emerald-100 shadow-lg">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.title}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Stats Section */}
      <section className="py-16 md:py-24 bg-white" aria-label="Statistics">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a4a1a] mb-2">
              Proven Track Record
            </h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto">
              Built on real results, not theory
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center bg-emerald-50/60 rounded-xl p-6 border border-emerald-100">
              <div className="text-3xl md:text-4xl font-bold text-emerald-700 mb-2">$20+</div>
              <p className="text-sm md:text-base text-slate-700 font-medium">Million in Islamic Financing Originated</p>
            </div>
            <div className="text-center bg-emerald-50/60 rounded-xl p-6 border border-emerald-100">
              <div className="text-3xl md:text-4xl font-bold text-emerald-700 mb-2">100%</div>
              <p className="text-sm md:text-base text-slate-700 font-medium">Portfolio Performance</p>
            </div>
            <div className="text-center bg-emerald-50/60 rounded-xl p-6 border border-emerald-100">
              <div className="text-3xl md:text-4xl font-bold text-emerald-700 mb-2">10K+</div>
              <p className="text-sm md:text-base text-slate-700 font-medium">Families Benefited</p>
            </div>
            <div className="text-center bg-emerald-50/60 rounded-xl p-6 border border-emerald-100">
              <div className="text-3xl md:text-4xl font-bold text-emerald-700 mb-2">2019</div>
              <p className="text-sm md:text-base text-slate-700 font-medium">Started Offering Islamic Financing</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-emerald-600 via-emerald-700 to-[#1a4a1a]" aria-label="Call to action">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Join the National Islamic Financing Platform
          </h2>
          <p className="text-lg text-white/95 mb-8 leading-relaxed">
            Join the national platform connecting institutions to riba-free capital. Deploy a market-proven model that expands access while keeping capital rooted in community impact.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={handleStartPartnerApplication}
              variant="outline"
              className="group !border-2 !border-white !text-white !bg-transparent hover:!bg-white hover:!text-emerald-700 hover:!border-white px-8 py-6 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              Sign Up to Launch Islamic Financing
              <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
          <p className="text-sm text-emerald-100 mt-5">
            Already a partner?{' '}
            <button 
              onClick={() => navigate('/partner-signin')} 
              className="underline font-semibold hover:text-white"
            >
              Sign in here
            </button>
          </p>
        </div>
      </section>
      
      {/* Fade-in Animation Styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
      `}</style>
    </main>
  );
};

export default PartnerWithUs;
